/**
 * Privacy Policy Page
 *
 * Renders full Privacy Policy from RED_SOCIAL_LEGAL_FRAMEWORK.md Section 2.
 * Includes AADC, SOPIPA (with aggregate analytics carve-out), CCPA 2026.
 *
 * Place at: src/pages/PrivacyPage.jsx
 */

import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Sparkles, ArrowLeft, Shield, MapPin, Lock, Database } from 'lucide-react';
import { cn } from '../utils/cn';

const EFFECTIVE_DATE = 'March 24, 2026';

/* ── Shared shell (same as TermsPage) ─────────────────────────────── */

const PageShell = ({ children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-300">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 left-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <header className="sticky top-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-all">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-semibold text-white">Enume</span>
          </Link>
        </div>
      </header>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-24">
        {children}
        <div className="mt-16 pt-8 border-t border-zinc-800/50 flex flex-wrap gap-4 text-xs text-zinc-500">
          <Link to="/terms" className="hover:text-zinc-300 transition-colors">Terms</Link>
          <span className="text-zinc-700">·</span>
          <Link to="/privacy" className="text-zinc-400">Privacy</Link>
          <span className="text-zinc-700">·</span>
          <Link to="/dmca" className="hover:text-zinc-300 transition-colors">DMCA</Link>
          <span className="text-zinc-700">·</span>
          <Link to="/register" className="hover:text-zinc-300 transition-colors">Create Account</Link>
          <span className="text-zinc-700">·</span>
          <Link to="/" className="hover:text-zinc-300 transition-colors">Home</Link>
        </div>
      </div>
    </div>
  );
};

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-20">
    <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
      <span className="text-indigo-400/60 text-sm font-mono">{id}</span>
      {title}
    </h2>
    <div className="space-y-3 text-zinc-400 text-sm leading-relaxed">{children}</div>
  </section>
);

const BulletList = ({ items }) => (
  <ul className="list-disc list-inside space-y-1 text-zinc-400 ml-2">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

/* ── Page ──────────────────────────────────────────────────────────── */

const PrivacyPage = () => (
  <PageShell>
    <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 tracking-tight">Privacy Policy</h1>
    <p className="text-zinc-500 text-sm mb-10">Effective {EFFECTIVE_DATE}</p>

    {/* Summary card */}
    <div className="mb-12 rounded-2xl border border-indigo-500/20 bg-indigo-500/5 p-5">
      <h2 className="text-base font-bold text-white mb-3 flex items-center gap-2">
        <Shield className="w-5 h-5 text-indigo-400" />
        The Short Version
      </h2>
      <p className="text-sm text-zinc-300">
        We collect only what's necessary to run the platform. We don't sell your personal data.
        We don't run ads. We don't track you across the web. Location is opt-in, per-project. Here's the full picture.
      </p>
    </div>

    <div className="space-y-8">

      <Section id="2.1" title="Information We Collect">
        <p className="text-zinc-200 font-medium">Information you provide directly:</p>
        <BulletList items={[
          'Account registration: email address, username, password (stored as bcrypt hash — we never see your plain-text password), date of birth (used solely to verify you are 18+; not displayed publicly)',
          'Profile information (optional): first name, last name, bio, profile picture, academic department, year, skills',
          'Projects and content: project files, images, descriptions, tags, challenge prompts, and challenge responses',
          'Messages: content of messages sent through the Platform\'s messaging system',
          'Wallet address (optional): if you connect a cryptocurrency wallet for marketplace features',
        ]} />

        <div className="flex gap-3 p-3 rounded-xl bg-zinc-800/30 border border-zinc-700/30 mt-3">
          <MapPin className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
          <p className="text-zinc-400">
            <span className="text-zinc-200 font-medium">Location data is opt-in only.</span>{' '}
            We do NOT automatically collect your location. You may optionally attach geographic coordinates to individual projects.
            Each project has a location privacy setting (exact, approximate, or hidden). You control this per-project and can change it at any time.
          </p>
        </div>

        <p className="text-zinc-200 font-medium mt-3">Information collected automatically:</p>
        <BulletList items={[
          'Basic server logs (IP address, browser type, access times) for security and debugging',
          'Authentication tokens stored in your browser\'s local storage to keep you signed in',
        ]} />
        <p className="text-zinc-500">We do NOT use third-party analytics trackers, advertising pixels, or cross-site tracking cookies.</p>
      </Section>

      <Section id="2.2" title="How We Use Your Information">
        <BulletList items={[
          'To provide and maintain the Platform',
          'To authenticate your identity and secure your account',
          'To display your profile and projects to other users',
          'To power lineage tracking, forking, and challenge features',
          'To send notifications about activity related to your projects',
          'To process marketplace transactions and revenue sharing',
          'To enforce our Terms of Service and protect against abuse',
        ]} />
      </Section>

      <Section id="2.3" title="Creative Commons and Public Content">
        <p>All projects are licensed under CC-BY-SA 4.0. Your uploaded projects — including names, descriptions, images, and files — are publicly visible by default. This is a core part of how the Platform functions, not a privacy violation.</p>
        <p>Your private information (email address, date of birth, password hash) is never shared publicly. Only your username, display name, profile picture, bio, and public project data are visible to others.</p>
      </Section>

      <Section id="2.4" title="Data Sharing">
        <p className="text-zinc-200 font-medium">We do not sell, rent, or trade your personal information.</p>
        <p>We may share data only in these limited circumstances:</p>
        <BulletList items={[
          'Service providers: hosting (Microsoft Azure), payment processing (Stripe) — minimum data necessary',
          'Legal requirements: if required by law, subpoena, or court order',
          'Safety: to prevent fraud, abuse, or threats to safety',
        ]} />
      </Section>

      <Section id="2.5" title="Data Retention">
        <p>We retain account data for as long as your account is active. Upon account deletion:</p>
        <BulletList items={[
          'Profile information, email, and date of birth are permanently deleted',
          'Messages are deleted',
          'Projects that have been forked remain with ghost attribution (username only)',
          'Projects with no forks or connections are fully deleted',
          'Server logs are retained for up to 90 days, then deleted',
        ]} />
      </Section>

      <Section id="2.6" title="Data Security">
        <BulletList items={[
          'Passwords stored using bcrypt hashing',
          'HTTPS encryption for all data in transit',
          'JWT-based authentication with secure token handling',
          'Database hosted on Azure with encryption at rest',
        ]} />
        <p>No system is perfectly secure. We take reasonable precautions but cannot guarantee absolute security.</p>
      </Section>

      <Section id="2.7" title="Your Rights">
        <BulletList items={[
          'Access your personal data (available in Profile and Settings)',
          'Correct inaccurate data (editable in Settings)',
          'Delete your account and associated personal data (available in Settings)',
          'Export your data — contact support@enume.io',
        ]} />
        <p>California residents may have additional rights under the CCPA. Contact support@enume.io to exercise privacy rights.</p>
      </Section>

      <Section id="2.8" title="Cookies & Tracking">
        <p>We use only essential cookies/tokens: authentication session tokens, security tokens (CSRF), and user preference storage (content ratings, theme). We do NOT use analytics cookies, advertising cookies, or third-party tracking.</p>
        <p>We honor Do Not Track (DNT) signals and Global Privacy Control (GPC) browser signals.</p>
      </Section>

      <Section id="2.9" title="Children's Privacy & Regulatory Compliance">
        <p className="text-zinc-200 font-medium">COPPA and Minors</p>
        <p>Enume is not intended for anyone under 18. We do not knowingly collect data from minors. If we discover a user is under 18, we will terminate the account and delete associated data.</p>

        <p className="text-zinc-200 font-medium mt-3">California Age-Appropriate Design Code Act (AADC)</p>
        <p>While restricted to users 18+, Enume acknowledges the AADC's broad applicability. The Platform implements age verification at registration, does not employ design patterns targeting minors, defaults privacy settings to the most protective options, and does not profile users in ways harmful to minors who may circumvent age gates.</p>

        <p className="text-zinc-200 font-medium mt-3">SOPIPA-Aligned Standards</p>
        <p>The Platform does not use individual student data for targeted advertising, does not sell individual user profiles, and does not build marketing profiles based on academic content.</p>
        <div className="p-3 rounded-xl bg-zinc-800/30 border border-zinc-700/30 mt-2">
          <p className="text-zinc-300 text-xs"><span className="text-zinc-200 font-medium">Aggregate Analytics Exception:</span> The Platform may provide anonymized, aggregate analytics to educational institutions, employers, and other partners (e.g., zone activity trends, skill demand signals, collaboration rates). These analytics cannot identify individual users. No personally identifiable information is included. If an institutional partnership requires access to identified student data, a separate Data Processing Agreement (DPA) will govern that relationship.</p>
        </div>

        <p className="text-zinc-200 font-medium mt-3">CCPA 2026 Compliance</p>
        <BulletList items={[
          'Requests to Know, Delete, and Correct are processed within 45 calendar days',
          'The Platform honors Global Privacy Control (GPC) browser signals',
          'Sensitive data collection defaults to "off" unless explicitly enabled by the user',
        ]} />
      </Section>

      <Section id="2.10" title="International Data Transfers">
        <p>Enume is operated from the United States. Your information may be transferred to, stored, and processed in the United States. By using Enume, you consent to international data transfers.</p>
      </Section>

      <Section id="2.11" title="Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. We will notify registered users of material changes via email or in-app notification. Continued use after changes constitutes acceptance.</p>
      </Section>

      <Section id="2.12" title="Contact">
        <p>Privacy questions: <a href="mailto:support@enume.io" className="text-purple-400 hover:text-purple-300 underline underline-offset-2">support@enume.io</a></p>
      </Section>

    </div>
  </PageShell>
);

export default PrivacyPage;